<template>
    <div class="product-list-page">

        <!-- 1. 顶部：产品类目 (Category) -->
        <div class="sub-nav">
            <div class="sub-item" v-for="category in categoryList" :key="category.id"
                :class="{ active: activeCategoryId === category.id }" @click="changeCategory(category.id)">
                <!-- 优先使用接口返回的图片，如果没有则用本地图片或者占位 -->
                <img class="sub-icon" :src="getAssetIcon(category.icon)"
                    v-if="category.icon || getAssetIcon(category.icon)" />
                <div class="sub-icon placeholder-icon" v-else>ICO</div>

                <span>{{ $t(`product.nav.` + category.name) }}</span>
            </div>
        </div>

        <main class="main-container">
            <!-- 2. 顶部产品主视觉 (Hero) -->
            <section class="product-hero">
                <div class="banner">
                    <img src="@/assets/products/banner.png" class="background-img" alt="Banner"
                        onerror="this.style.display='none'">
                    <div class="banner-placeholder"></div>
                </div>
            </section>

            <!-- 3. 主体左右分栏布局 -->
            <section class="layout-wrapper">
                <!-- 左侧：产品类型 (Type/Model) -->
                <aside class="sidebar">
                    <div class="category-group">
                        <h3 class="category-title active">
                            {{ $t(`product.nav.` + currentCategoryName) }}
                        </h3>
                        <ul class="sub-category-list" v-if="typeList.length > 0">
                            <li v-for="type in typeList" :key="type.id" :class="{ 'active': activeTypeId === type.id }"
                                @click="changeType(type.id)">
                                {{ $t(`product.sub.` + type.name) }}
                            </li>
                        </ul>
                        <div v-else class="empty-text">
                            {{ $t('product.common.emptyProduct') }}
                        </div>
                    </div>
                </aside>

                <!-- 右侧：产品展示与详细内容 -->
                <article class="content-area">
                    <!-- 分类标题与描述 -->
                    <div class="category-header">
                        <h1>
                            {{ $t(`product.nav.` + currentCategoryName) }}
                            <span v-if="activeTypeId">- {{ $t(`product.sub.` + currentTypeName) }}</span>
                        </h1>
                    </div>

                    <!-- 产品网格 (直接使用接口返回的产品列表) -->
                    <div class="product-grid" v-if="productList.length > 0">
                        <div class="product-card" v-for="product in productList" :key="product.id">
                            <div class="card-img">
                                <!-- 替换为接口返回的产品主图 -->
                                <img style="height: 100%;width: 100%;object-fit: contain;"
                                    :src="`https://qiniu-au.wifigsmalarm.com/images/` + product.icon + `.png`" />
                            </div>
                            <h4 class="card-title">{{ product.title || product.name }}</h4>
                            <p class="c ard-desc">{{ locale === 'zh' ? '￥' + (product.price * 6.8).toFixed(2) : 'US$' +
                                product.price.toFixed(2) }}</p>
                            <div class="card-actions">
                                <button class="btn-outline" @click="openModal(product)">{{
                                    $t('product.common.learnMore') }}</button>
                                <button class="btn-outline text-cyan" @click="addToCart(product)">
                                    {{ $t('product.common.addToCart') }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- 缺省状态 -->
                    <div class="empty-state" v-else>
                        <p>{{ $t('product.common.emptyProduct') }}</p>
                    </div>
                </article>
            </section>
        </main>

        <!-- ================= 详情弹窗 ================= -->
        <transition name="fade">
            <div class="modal-overlay" v-if="isModalOpen" @click="closeModal">
                <!-- 加上 @click.stop 防止点击弹窗内部时触发了外层蒙层的关闭事件 -->
                <div class="modal-content" @click.stop>
                    <div class="modal-header">
                        <button class="close-btn" @click="closeModal">×</button>
                    </div>

                    <div class="modal-body">
                        <div class="modal-desc rich-text-container" v-html="currentProduct.description"></div>
                    </div>
                    <div class="modal-footer">
                        <button class="btn-outline text-cyan" @click="addToCartFromModal">
                            {{ $t('product.common.addToCart') }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- ================= 购物车功能区 ================= -->

        <!-- 悬浮购物车入口 -->
        <div class="floating-cart" @click="isCartOpen = true">
            <span class="cart-icon">🛒</span>
            <span class="cart-badge" v-if="totalItems > 0">{{ totalItems }}</span>
        </div>

        <!-- 购物车抽屉蒙层 -->
        <div class="cart-overlay" v-if="isCartOpen" @click="isCartOpen = false"></div>

        <!-- 购物车侧边抽屉 -->
        <div class="cart-drawer" :class="{ 'open': isCartOpen }">
            <div class="cart-header">
                <div class="header-left">
                    <button class="back-btn" v-if="cartStep === 2" @click="cartStep = 1">←</button>
                    <h2>{{ cartStep === 1 ? $t('product.cart.titleList') : $t('product.cart.titleForm') }}</h2>
                </div>
                <button class="close-btn" @click="isCartOpen = false">×</button>
            </div>

            <div class="cart-body">
                <!-- Step 1: 产品列表与数量调整 -->
                <div v-if="cartStep === 1">
                    <div v-if="cartList.length === 0" class="empty-cart">
                        <p>{{ $t('product.cart.empty') }}</p>
                    </div>
                    <div v-else class="cart-list">
                        <div class="cart-item" v-for="(item, index) in cartList" :key="item.id">
                            <div class="item-img">
                                <img style="height: 100%;width: 100%;object-fit: contain;"
                                    :src="`https://qiniu-au.wifigsmalarm.com/images/` + item.icon + `.png`" />
                                <span>{{ locale === 'zh' ? '￥' + (item.price * 6.8).toFixed(2) : 'US$' +
                                    item.price.toFixed(2) }}</span>
                            </div>

                            <div class="item-info">
                                <h4>{{ item.title || item.name }}</h4>
                                <p>{{ $t(`product.nav.` + item.categoryName) }} <span v-if="item.typeName">- {{
                                    $t(`product.sub.` + item.typeName) }}</span></p>
                                <!-- 数量控制 -->
                                <div class="qty-control">
                                    <button @click="decreaseQty(index)">-</button>
                                    <input type="number" v-model.number="item.quantity" min="1" readonly />
                                    <button @click="increaseQty(index)">+</button>
                                </div>
                            </div>
                            <button class="remove-btn" @click="removeFromCart(index)">{{ $t('product.cart.delete')
                                }}</button>
                        </div>
                    </div>
                </div>

                <!-- Step 2: 客户信息表单 -->
                <div v-if="cartStep === 2" class="checkout-form">
                    <div class="form-group">
                        <label>{{ $t('product.form.name') }} <span>*</span></label>
                        <input type="text" v-model="formData.name" :placeholder="$t('product.form.namePlaceholder')" />
                    </div>
                    <div class="form-group">
                        <label>{{ $t('product.form.address') }}</label>
                        <input type="text" v-model="formData.address"
                            :placeholder="$t('product.form.addressPlaceholder')" />
                    </div>
                    <div class="form-group">
                        <label>{{ $t('product.form.location') }} <span>*</span></label>
                        <input type="text" v-model="formData.area"
                            :placeholder="$t('product.form.locationPlaceholder')" />
                    </div>
                    <div class="form-group">
                        <label>{{ $t('product.form.whatsapp') }} </label>
                        <input type="text" v-model="formData.whatsapp"
                            :placeholder="$t('product.form.whatsappPlaceholder')" />
                    </div>
                    <div class="form-group">
                        <label>{{ $t('product.form.email') }} </label>
                        <input type="text" v-model="formData.email"
                            :placeholder="$t('product.form.emailPlaceholder')" />
                    </div>
                    <div class="form-group">
                        <label>{{ $t('product.form.remark') }} </label>
                        <textarea v-model="formData.remark" style="height: 200px;"
                            :placeholder="$t('product.form.remarkPlaceholder')"></textarea>
                    </div>
                </div>
            </div>

            <div class="cart-footer">
                <!-- Step 1 底部按钮 -->
                <template v-if="cartStep === 1">
                    <h3>{{ totalPrice }}</h3>
                    <div class="cart-total">{{ $t('product.cart.totalItems', { count: totalItems }) }}</div>
                    <button class="btn-submit" :disabled="cartList.length === 0" @click="cartStep = 2">
                        {{ $t('product.cart.nextStep') }}
                    </button>
                </template>

                <!-- Step 2 底部按钮 -->
                <template v-if="cartStep === 2">
                    <button class="btn-submit" @click="submitApplication">
                        {{ $t('product.cart.submit') }}
                    </button>
                </template>
            </div>
        </div>

    </div>
</template>

<script setup>
import { ref, computed, reactive, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { TypeList, ModelList, List, Order } from '@/api/product'

const { t, locale } = useI18n()

// ================= 详情弹窗逻辑 =================
const isModalOpen = ref(false)
const currentProduct = ref('') // 存放当前点击的产品信息

// 打开弹窗
const openModal = (product) => {
    currentProduct.value = product
    isModalOpen.value = true
}

// 关闭弹窗
const closeModal = () => {
    isModalOpen.value = false
    // 稍微延迟清空数据，防止关闭动画执行时看到内容突然消失
    setTimeout(() => {
        currentProduct.value = {}
    }, 300)
}

// 可选优化：在弹窗里点击“加入购物车”后，加入成功并顺便关掉弹窗
const addToCartFromModal = () => {
    addToCart(currentProduct.value)
    closeModal()
}

// 动态获取本地兜底图标
const iconModules = import.meta.glob('../assets/icons/*.png', { eager: true });
const getAssetIcon = (name) => {
    if (!name) return '';
    const globPath = `../assets/icons/${name}.png`;
    if (iconModules[globPath]) {
        return iconModules[globPath].default;
    }
    return '';
}

// ================= 数据结构与状态管理 =================
const categoryList = ref([]) // 接口返回的数据：顶级类目 (Top Nav)
const typeList = ref([])     // 接口返回的数据：产品类型 (Sidebar)
const productList = ref([])  // 接口返回的数据：产品列表 (Grid)

const activeCategoryId = ref('')
const activeTypeId = ref('')

onMounted(() => {
    // 页面加载完成立即请求顶级类目
    getCategoryData()
})

// 1. 获取顶级类目
const getCategoryData = async () => {
    try {
        const result = await TypeList()
        // 修复原代码 Bug：必须用 .value 赋值才能触发响应式更新
        categoryList.value = result || []
        // 如果有数据，默认选中第一项
        if (categoryList.value.length > 0) {
            changeCategory(categoryList.value[0].id)
        }
    } catch (error) {
        console.error('获取类目失败：', error)
    }
}

// 2. 根据选中的类目，获取对应的类型
const getTypeData = async (categoryId) => {
    try {
        const params = { 'type_id': categoryId }
        const result = await ModelList(params)
        typeList.value = result || []

        if (typeList.value.length > 0) {
            // 获取到类型后，默认选中第一个类型
            changeType(typeList.value[0].id)
        } else {
            // 如果该类目下没有类型，清空当前类型ID并直接去拉取产品数据
            activeTypeId.value = ''
            getProductData()
        }
    } catch (error) {
        console.error('获取类型失败：', error)
    }
}

// 3. 根基类目和类型，获取最终产品列表
const getProductData = async () => {
    try {
        const params = {
            'type_id': activeCategoryId.value,
            'model_id': activeTypeId.value
        }
        const result = await List(params)
        productList.value = result || []
    } catch (error) {
        console.error('获取产品失败：', error)
    }
}

// ================= 交互动作 =================

// 切换顶级类目
const changeCategory = (id) => {
    if (activeCategoryId.value === id) return
    activeCategoryId.value = id
    // 类目变化，去拉取下一级的类型
    getTypeData(id)
}

// 切换产品类型
const changeType = (id) => {
    if (activeTypeId.value === id) return
    activeTypeId.value = id
    // 类型变化，去拉取产品
    getProductData()
}

// 计算当前选中的类目和类型名称（用于展示在页面标题和购物车中）
const currentCategoryName = computed(() => {
    const cat = categoryList.value.find(c => c.id === activeCategoryId.value)
    return cat ? cat.name : ''
})

const currentTypeName = computed(() => {
    const type = typeList.value.find(t => t.id === activeTypeId.value)
    return type ? type.name : ''
})

// ================= 购物车与表单逻辑 =================

const cartList = ref([])
const isCartOpen = ref(false)
const cartStep = ref(1)

const formData = reactive({
    name: '',
    address: '',
    area: '',
    whatsapp: '',
    email: '',
    remark: ''
})

const totalItems = computed(() => {
    return cartList.value.reduce((sum, item) => sum + item.quantity, 0)
})

const totalPrice = computed(() => {
    const total = cartList.value.reduce((sum, item) => sum + item.price * item.quantity, 0)
    return locale.value === 'zh' ? `￥${(total * 6.8).toFixed(2)}` : `$${total.toFixed(2)}`
})

const addToCart = (product) => {
    const existingItem = cartList.value.find(item => item.id === product.id)
    if (existingItem) {
        existingItem.quantity += 1
    } else {
        cartList.value.push({
            ...product,
            quantity: 1,
            // 附加当前的分类和类型名称，方便在购物车列表展示
            categoryName: currentCategoryName.value,
            typeName: currentTypeName.value
        })
    }
    isCartOpen.value = true
}

const increaseQty = (index) => {
    cartList.value[index].quantity++
}

const decreaseQty = (index) => {
    if (cartList.value[index].quantity > 1) {
        cartList.value[index].quantity--
    } else {
        removeFromCart(index)
    }
}

const removeFromCart = (index) => {
    cartList.value.splice(index, 1)
    if (cartList.value.length === 0) {
        cartStep.value = 1
    }
}

const submitApplication = async () => {
    if (!formData.name || !formData.area) {
        alert(t('alert.fillRequired'))
        return
    }

    const finalData = {
        list: cartList.value,
        ...formData
    }

    try {
        await Order(finalData)
        alert(t('alert.submitSuccess'))
        // 提交成功，清空重置
        cartList.value = []
        Object.keys(formData).forEach(key => formData[key] = '')
        cartStep.value = 1
        isCartOpen.value = false
    } catch (error) {
        console.error('提交订单失败：', error)
    }
}
</script>

<style scoped>
/* =========================================
   全局与基础样式
========================================= */
.product-list-page {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: #333;
    position: relative;
}

.empty-text {
    color: #999;
    font-size: 0.9rem;
    padding-left: 15px;
    margin-top: 10px;
}

.lang-switch-bar {
    position: absolute;
    top: 15px;
    right: 40px;
    z-index: 10;
}

.btn-lang {
    background: #f0f0f0;
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9rem;
}

.btn-lang:hover {
    background: #e0e0e0;
}

.placeholder-img,
.placeholder-icon {
    background-color: #eaeaea;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
    border-radius: 4px;
}

/* =========================================
   Header & SubNav 样式
========================================= */
.sub-nav {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 100px;
    padding: 100px 40px 40px 40px;
    border-bottom: 1px solid #f0f0f0;
    background: #fff;
}

.sub-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    font-size: 0.85rem;
    color: #666;
    transition: all 0.2s;
}

.sub-item:hover {
    color: #00bcd4;
}

.sub-item.active {
    font-weight: bold;
    color: #000;
}

.sub-icon {
    width: 35px;
    height: 35px;
    margin-bottom: 8px;
    object-fit: contain;
}

/* =========================================
   页面主体布局
========================================= */
.main-container {
    width: 100%;
}

.product-hero {
    height: 40vh;
    min-height: 300px;
}

.banner {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: #2a2a2a;
}

.background-img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.layout-wrapper {
    max-width: 1400px;
    margin: 0 auto;
    display: flex;
    padding: 60px 40px;
    gap: 60px;
}

/* =========================================
   左侧：侧边栏分类
========================================= */
.sidebar {
    width: 200px;
    flex-shrink: 0;
    position: sticky;
    top: 60px;
    height: fit-content;
}

.category-group {
    margin-bottom: 20px;
}

.category-title {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 12px;
    cursor: pointer;
    color: #1a1a1a;
    transition: color 0.2s;
}

.category-title:hover {
    color: #00bcd4;
}

.category-title.active {
    color: #00bcd4;
}

.sub-category-list {
    list-style: none;
    padding: 0;
    margin: 0 0 0 10px;
    border-left: 2px solid #eee;
}

.sub-category-list li {
    font-size: 0.9rem;
    color: #666;
    padding: 8px 0 8px 15px;
    cursor: pointer;
    transition: all 0.2s;
}

.sub-category-list li:hover {
    color: #000;
}

.sub-category-list li.active {
    color: #00bcd4;
    font-weight: 600;
}

/* =========================================
   右侧：内容区与产品网格
========================================= */
.content-area {
    flex: 1;
    min-width: 0;
}

.category-header {
    margin-bottom: 40px;
    border-bottom: 1px solid #eaeaea;
    padding-bottom: 20px;
}

.category-header h1 {
    font-size: 2rem;
    margin: 0 0 10px 0;
}

.category-header p {
    color: #666;
    font-size: 1rem;
    margin: 0;
}

/* 产品网格 */
.product-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 30px;
    margin-bottom: 80px;
}

.product-card {
    background: #fff;
    border: 1px solid #f0f0f0;
    padding: 30px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    border-radius: 8px;
    transition: all 0.3s ease;
}

.product-card:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.08);
    transform: translateY(-5px);
}

.card-img {
    width: 80%;
    aspect-ratio: 4/3;
    margin-bottom: 25px;
}

.card-title {
    font-size: 1.2rem;
    font-weight: 700;
    margin: 0 0 10px 0;
}

.card-desc {
    font-size: 0.85rem;
    color: #888;
    line-height: 1.4;
    margin: 0 0 25px 0;
    height: 40px;
    overflow: hidden;
}

.card-actions {
    display: flex;
    gap: 10px;
    margin-top: auto;
    width: 100%;
    justify-content: center;
}

.btn-outline {
    padding: 8px 16px;
    border: 1px solid #d9d9d9;
    background: transparent;
    border-radius: 20px;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s;
    color: #333;
}

.btn-outline:hover {
    border-color: #999;
}

.btn-outline.text-cyan {
    color: #00bcd4;
    border-color: #00bcd4;
}

.btn-outline.text-cyan:hover {
    background: #00bcd4;
    color: #fff;
}

.empty-state {
    padding: 60px 20px;
    text-align: center;
    color: #999;
    font-size: 1.1rem;
    background: #f9f9f9;
    border-radius: 8px;
    margin-bottom: 80px;
}

.bottom-info-section {
    border-top: 1px solid #eaeaea;
    padding-top: 40px;
}

/* =========================================
   购物车悬浮按钮与抽屉样式
========================================= */
.floating-cart {
    position: fixed;
    right: 40px;
    bottom: 80px;
    width: 60px;
    height: 60px;
    background-color: #ffffff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
    z-index: 99;
    transition: transform 0.2s ease;
}

.floating-cart:hover {
    transform: scale(1.05);
}

.cart-icon {
    font-size: 1.5rem;
}

.cart-badge {
    position: absolute;
    top: -5px;
    right: -5px;
    background-color: #ff4d4f;
    color: white;
    font-size: 0.75rem;
    font-weight: bold;
    min-width: 20px;
    height: 20px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 5px;
    border: 2px solid #fff;
}

.cart-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    z-index: 100;
}

.cart-drawer {
    position: fixed;
    top: 0;
    right: -400px;
    width: 400px;
    height: 100vh;
    background: #fff;
    z-index: 101;
    display: flex;
    flex-direction: column;
    box-shadow: -5px 0 15px rgba(0, 0, 0, 0.1);
    transition: right 0.3s ease;
}

.cart-drawer.open {
    right: 0;
}

.cart-header {
    padding: 20px;
    border-bottom: 1px solid #eaeaea;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 10px;
}

.header-left h2 {
    margin: 0;
    font-size: 1.2rem;
}

.back-btn {
    background: none;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
    color: #666;
}

.back-btn:hover {
    color: #00bcd4;
}

.close-btn {
    background: none;
    border: none;
    font-size: 1.8rem;
    cursor: pointer;
    color: #999;
}

.close-btn:hover {
    color: #333;
}

.cart-body {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    background: #f9f9f9;
}

.empty-cart {
    text-align: center;
    color: #999;
    margin-top: 50px;
}

.cart-list {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.cart-item {
    background: #fff;
    padding: 15px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 15px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.02);
}

.item-img {
    width: 60px;
    height: 60px;
    /* background-color: #eee; */
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    color: #aaa;
    flex-shrink: 0;
    flex-direction: column;
}

.item-info {
    flex: 1;
}

.item-info h4 {
    margin: 0 0 5px 0;
    font-size: 1rem;
    color: #333;
}

.item-info p {
    margin: 0;
    font-size: 0.8rem;
    color: #666;
}

.qty-control {
    display: flex;
    align-items: center;
    margin-top: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    width: fit-content;
    overflow: hidden;
    margin-left: 25%;
}

.qty-control button {
    background: #f5f5f5;
    border: none;
    width: 28px;
    height: 28px;
    cursor: pointer;
    font-size: 1rem;
    color: #555;
}

.qty-control button:hover {
    background: #e0e0e0;
}

.qty-control input {
    width: 40px;
    height: 28px;
    border: none;
    text-align: center;
    font-size: 0.9rem;
    border-left: 1px solid #ddd;
    border-right: 1px solid #ddd;
    outline: none;
    background: #fff;
}

.remove-btn {
    background: none;
    border: none;
    color: #ff4d4f;
    cursor: pointer;
    font-size: 0.85rem;
}

.remove-btn:hover {
    text-decoration: underline;
}

.checkout-form {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.form-group label {
    font-size: 0.9rem;
    color: #333;
    font-weight: 500;
}

.form-group label span {
    color: #ff4d4f;
}

.form-group input,
.form-group textarea {
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.95rem;
    outline: none;
}

.form-group input:focus,
.form-group textarea:focus {
    border-color: #00bcd4;
}

.cart-footer {
    padding: 20px;
    border-top: 1px solid #eaeaea;
    background: #fff;
}

.cart-total {
    margin-bottom: 15px;
    font-size: 0.95rem;
    color: #666;
    font-weight: bold;
}

.btn-submit {
    width: 100%;
    padding: 12px 0;
    background: #00bcd4;
    color: #fff;
    border: none;
    border-radius: 4px;
    font-size: 1rem;
    cursor: pointer;
    transition: background 0.2s;
}

.btn-submit:hover:not(:disabled) {
    background: #00acc1;
}

.btn-submit:disabled {
    background: #ccc;
    cursor: not-allowed;
}

/* 响应式调整 */
@media (max-width: 768px) {
    .layout-wrapper {
        flex-direction: column;
        padding: 20px;
    }

    .sidebar {
        width: 100%;
        position: static;
        margin-bottom: 30px;
    }

    .cart-drawer {
        width: 100%;
        right: -100%;
    }
}

/* =========================================
   产品详情弹窗 (Modal) 样式
========================================= */
/* 1. 遮罩层：确保铺满全屏，并利用 flex 完美全局居中 */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    /* 半透明黑底 */
    display: flex;
    align-items: center;
    /* 垂直居中 */
    justify-content: center;
    /* 水平居中 */
    z-index: 9999;
    /* 提高层级，确保盖住导航栏等所有元素 */
}

/* 2. 弹窗本体：增加明确的边框 */
.modal-content {
    background: #fff;
    width: 90%;
    max-width: 500px;
    /* 限制最大宽度 */
    border-radius: 12px;
    border: 1px solid #d9d9d9;
    /* ✨ 这里加上了灰色的实体边框 */
    padding: 25px;
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
    /* 配合边框，阴影稍微调柔和一些 */
    display: flex;
    flex-direction: column;
}

/* ... 保持 modal-header 等其他代码不变 ... */
.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #eaeaea;
    padding-bottom: 15px;
    margin-bottom: 15px;
}

.modal-header h3 {
    margin: 0;
    font-size: 1.3rem;
    color: #333;
}

.modal-header .close-btn {
    background: none;
    border: none;
    font-size: 2rem;
    line-height: 1;
    cursor: pointer;
    color: #999;
}

.modal-header .close-btn:hover {
    color: #333;
}

.modal-body {
    flex: 1;
    max-height: 60vh;
    overflow-y: auto;
}

/* 3. 文字描述：强制自动换行，防止长文本撑破弹窗 */
.modal-desc {
    font-size: 0.95rem;
    color: #666;
    line-height: 1.6;
    margin: 0;

    /* ✨ 核心：强制自动换行组合拳 ✨ */
    white-space: pre-wrap;
    /* 既保留接口里的 \n 换行，又允许正常的文本折行 */
    word-wrap: break-word;
    /* 允许超长单词内部进行换行 */
    word-break: break-word;
    /* 防止一长串没有空格的字符（如链接、型号）直接顶出屏幕 */
}

.modal-footer {
    margin-top: 25px;
    display: flex;
    justify-content: flex-end;
}

/* 弹窗动画保持不变 */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.fade-enter-active .modal-content,
.fade-leave-active .modal-content {
    transition: transform 0.3s ease;
}

.fade-enter-from .modal-content,
.fade-leave-to .modal-content {
    transform: translateY(-20px) scale(0.95);
}

/* =========================================
   富文本渲染样式 (Rich Text)
========================================= */
.rich-text-container {
    font-size: 0.95rem;
    color: #444;
    line-height: 1.8;
    text-align: left;
}

/* 使用 :deep() 穿透 scoped 作用域，对 v-html 内部的元素设置样式 */
.rich-text-container :deep(p) {
    margin-top: 0;
    margin-bottom: 1em;
}

.rich-text-container :deep(img) {
    max-width: 100%;
    /* 确保富文本里的图片不会撑破弹窗 */
    height: auto;
    border-radius: 8px;
    margin: 10px 0;
}

.rich-text-container :deep(ul),
.rich-text-container :deep(ol) {
    padding-left: 20px;
    margin-bottom: 1em;
}

.rich-text-container :deep(a) {
    color: #00bcd4;
    text-decoration: none;
}

.rich-text-container :deep(a:hover) {
    text-decoration: underline;
}
</style>